import AdAdapter from './adapters/ad_adapter';
import AhoyAdapter from "./adapters/ahoy_adapter";
import CookieUtils from './utils/cookie_utils';
import { isFeatureToggleOff } from "./utils/feature_toggle";

export class PlayerIMA {
  constructor(tech, data, uuidManager){
    this._tech = tech;
    this._data = data;
    this._uuidManager = uuidManager;
    this._previousAdCurrentTime = 0;
    this._currentAd = null;
    this._adCountingDown = false;
    this._isTvcReplacementStarted = false;
    this._isTvcReplacement = false;
  }

  tech(){
    return this._tech;
  }

  data() {
    return this._data;
  }

  uuidManager() {
    return this._uuidManager;
  }

  player() {
    return this.tech().plugin().instance();
  }

  adState() {
    return this.player().ads._state;
  }

  adsManager(){
    return this.ima().getAdsManager();
  }

  adTitle(){
    const currentAd = this.currentAd();
    return currentAd.ad() === null ? '' : currentAd.getTitle();
  }

  adSkippable(){
    return this.adsManager().getAdSkippableState();
  }

  currentAdWidth(){
    return this.currentAd().getWidth();
  }

  currentAdHeight(){
    return this.currentAd().getHeight();
  }

  currentAdDuration() {
    return this.currentAd().getDuration();
  }

  currentAd(){
    return this._currentAd;
  }

  isAdPaused(){
    return this._adPaused;
  }

  adVolume() {
    return this._adVolume;
  }

  adRemainingTime(){
    if (this.adsManager() == null) {
      return -1;
    }

    const remainingTime = this.adsManager().getRemainingTime()
    if (remainingTime === 0 && !this._adCountingDown) {
      return -1;
    }
    if (remainingTime > 0) {
      this._adCountingDown = true;
    }
    return remainingTime;
  }

  adCurrentTime(){
    const adRemainingTime = this.adRemainingTime();
    if(adRemainingTime < 0) {
      return 0;
    }
    return Math.ceil(this.currentAdDuration() - adRemainingTime);
  }

  containerEl(){
    const playerEl = this.tech().el();
    return playerEl.querySelector(".ima-ad-container");
  }

  ima(){
    return this.tech().plugin().imaObject();
  }

  initializeAdDisplayContainer(){
    if (typeof this.ima().initializeAdDisplayContainer === 'function') {
      this.ima().initializeAdDisplayContainer();
    }
  }

  requestAds(){
    this.ima().requestAds();
  }

  defaultOptions() {
    const options = {
      prerollTimeout: window.prerollTimeout || 5000,
      adsRenderingSettings: {
        bitrate: this.data().adPreferredBitrate(),
        loadVideoTimeout: this.data().vastMediaDownloadTimeout(),
        enablePreloading: true,
      },
      disableCustomPlaybackForIOS10Plus: false,
    }

    const visitorId = CookieUtils.getCookie('he_visitor_id') || AhoyAdapter.global().visitorId

    if(visitorId) {
      let ppid = visitorId.replace(/-/g, '');
      ppid += "vidio";

      Object.assign(options, {
        ppid: ppid
      });
    }

    return options;
  }

  setup(id, adTagUrl){
    const options = Object.assign({
      id,
      adTagUrl
    }, this.defaultOptions());

    this.setupIma(options);
  }

  setupIma(options){
    this.tech().plugin().imaSetup(options);
    this.setupAdEvents();
  }

  imaAlreadySetup(){
    return typeof this.tech().plugin().imaObject() === 'object';
  }

  request(){
    this.initializeAdDisplayContainer();
    this.requestAds();
  }

  addEventListener(event, callback){
    this.ima().addEventListener(event, callback);
  }

  removeEventListener(event, callback){
    this.ima().removeEventListener(event, callback);
  }

  resume(){
     this.ima().onContentResumeRequested_();
  }

  skip(){
    this.adsManager().skip();
  }

  setupAdEvents(){
    this.tech().on("adsready", function(){
      this.setupAdEventsAfterAdsReady();
    }.bind(this));
  }

  setupAdEventsAfterAdsReady(){
    this.setupAdLoadedEvent();
    this.setupDurationChangedEvent();
    this.setupAdBufferEvent();
    this.setupAdClickEvent();
    this.setupAdCompleteEvent();
    this.setupAdSkippedEvent();
    this.setupAdPausedEvent();
    this.setupAdWatchEvent();
    this.setupImaStartEvent();
    this.setupImaFirstQuartileEvent();
    this.setupImaMidpointEvent();
    this.setupImaThirdQuartileEvent();
  }

  setupImaStartEvent(){
    this.addEventListener(google.ima.AdEvent.Type.STARTED, this._onImaStart.bind(this));
  }

  _onImaStart(e){
    const podIndex = e.getAd().getAdPodInfo().getPodIndex();
    let adType;

    if (podIndex === 0) {
      adType = 'pre';
    } else if (podIndex === -1) {
      adType = 'post';
    } else {
      adType = 'mid';
    }
    this._previousAdCurrentTime = 0;

    this.tech().trigger({ type: 'adstarted', adEvent: e });
    if (this.data().isVideo()) {
      this.tech().trigger({
        type: "vp-video-adstart",
        id: this.data().videoId(),
        embed: this.data().embed(),
        adUUID: this.uuidManager().uuid(),
      });
    } else {
      this.tech().trigger({
        type: "vp-livestream-adstart",
        id: this.data().videoId(),
        embed: this.data().embed(),
        adUUID: this.uuidManager().uuid(),
      });
    }
    if (e.getAd().isLinear()) {
      this.tech().trigger({ type: 'adstart_linear', adType, adEvent: e });
    } else {
      this.tech().trigger({ type: "vp-ima-start", width: this.currentAdWidth(), height: this.currentAdHeight() });
    }
  }

  setupDurationChangedEvent() {
    this.addEventListener(google.ima.AdEvent.Type.DURATION_CHANGE, this._onAdDurationChanged.bind(this));
  }

  _onAdDurationChanged(e) {
    this._currentAd = new AdAdapter(e.getAd());
  }

  setupAdLoadedEvent() {
    this.addEventListener(google.ima.AdEvent.Type.LOADED, this._onAdLoaded.bind(this));
  }

  _onAdLoaded(e) {
    this._currentAd = new AdAdapter(e.getAd());
    this._adCountingDown = false;
    this.tech().trigger({ type: 'adloaded', ad: this._currentAd });

    if (this.currentAd().isLinear() === false) {
      this.resumeContentPlayback();
      this.resizeAdContainer();
    }
  }

  setupTvcReplacementEvent() {
    if (isFeatureToggleOff('ads_tvc_seek_to_latest')) {
      return;
    }

    this.tech().on('ads-manager', () => {
      this._isTvcReplacement = true;
      this.setupTvcEventOnAdStarted();
      this.setupTvcEventOnAdCompleted();
      this.setupTvcEventOnSeeked();
    })
  }
  
  setupTvcEventOnAdStarted() {
    this.addEventListener(google.ima.AdEvent.Type.STARTED, this.onTvcReplacementStarted.bind(this));
  }
  
  setupTvcEventOnAdCompleted() {
    this.addEventListener(google.ima.AdEvent.Type.ALL_ADS_COMPLETED, this.seekToUnbufferedLatestSegment.bind(this));
  }

  setupTvcEventOnSeeked() {
    this.tech().on('seeked', () => {
      this.tech().play();
    });
  }

  onTvcReplacementStarted() {
    if (this._isTvcReplacement === false) {
      return;
    }

    this._isTvcReplacementStarted = true;
    this._isTvcReplacement = false;
  }

  seekToUnbufferedLatestSegment() {
    if (this._isTvcReplacementStarted) {
      this.tech().currentTime(Number.MAX_SAFE_INTEGER);
      this._isTvcReplacementStarted = false;
    }
  }

  resumeContentPlayback() {
    this.adState().resumeAfterNoPreroll?.(this.player());
  }

  resizeAdContainer() {
    const container = this.containerEl().el();
    const currentAd = this.currentAd();
    const controlBar = this.tech().el().querySelector('.vjs-control-bar');
    container.style.width = currentAd.getWidth() + 'px';
    container.style.height = currentAd.getHeight() + 'px';
    //TODO: Add test to cover top value
    container.style.top = (this.tech().el().clientHeight() - currentAd.getHeight() - controlBar.clientHeight()) + 'px';
    container.style.transition = 'margin-top 1s ease';

    this.stopAdAfter10s();
  }

  stopAdAfter10s() {
    window.setTimeout(() => {
      this.adsManager().stop();
    }, 10 * 1000);
  }

  setupAdBufferEvent() {
    this.addEventListener(google.ima.AdEvent.Type.AD_BUFFERING, this._onAdBuffer.bind(this));
  }

  _onAdBuffer() {
    this.tech().trigger({ type: 'adbuffer', currentTime: this.adCurrentTime() });
  }

  setupAdClickEvent(){
    this.addEventListener(google.ima.AdEvent.Type.CLICK, this._onAdClick.bind(this));
  }

  _onAdClick(){
    this.tech().trigger({ type: "adclick", currentTime: this.adCurrentTime() });
    if(this.isAdPaused()){
      this.adsManager().resume()
    } else {
      this.adsManager().pause()
    }
  }

  setupAdCompleteEvent(){
    this.addEventListener(google.ima.AdEvent.Type.COMPLETE, this._onAdComplete.bind(this));
  }

  _onAdComplete(){
    this.tech().trigger({ type: "adcomplete", currentTime: this.currentAdDuration()});
  }

  setupAdSkippedEvent(){
    this.addEventListener(google.ima.AdEvent.Type.SKIPPED, this._onAdSkipped.bind(this));
  }

  _onAdSkipped(){
    this.tech().trigger({ type: "adskipped", currentTime: this.adCurrentTime() });
  }

  setupAdWatchEvent(){
    this.tech().on("adstarted", function(){
      if (this._adWatchInterval) {
        clearInterval(this._adWatchInterval);
      }

      this._adWatchInterval = setInterval(function(){
        if(!this.isAdPaused()){
          this._onAdWatch();
        }
      }.bind(this), 1000);
    }.bind(this));

    this.tech().on("adend", function(){
      clearInterval(this._adWatchInterval);
    }.bind(this));
    this.tech().on("ended", function(){
      clearInterval(this._adWatchInterval);
    }.bind(this));
    this.tech().on("dispose", function(){
      clearInterval(this._adWatchInterval);
    }.bind(this));
    this.tech().on("adskipped", function(){
      clearInterval(this._adWatchInterval);
    }.bind(this));
  }

  _onAdWatch(){
    const adCurrentTime = this.adCurrentTime();
    if(this._previousAdCurrentTime < adCurrentTime){
      this.tech().trigger({
        type: "adwatch",
        data:
        {
          adIndex: this.currentAd().getPodIndex(),
          adPosition: this.currentAd().getPodAdPosition(),
          duration: this.currentAdDuration(),
          currentTime: adCurrentTime
        }
      });
      this._previousAdCurrentTime = adCurrentTime;
    }
  }

  setupAdPausedEvent(){
    this.addEventListener(google.ima.AdEvent.Type.STARTED, function(){
      this._adPaused = false;
    }.bind(this));

    this.addEventListener(google.ima.AdEvent.Type.RESUMED, function(){
      this._adPaused = false;
    }.bind(this));

    this.addEventListener(google.ima.AdEvent.Type.PAUSED, function(){
      this._adPaused = true;
    }.bind(this));
  }

  setupImaFirstQuartileEvent() {
    this.addEventListener(google.ima.AdEvent.Type.FIRST_QUARTILE, this.onImaFirstQuartile.bind(this));
  }

  onImaFirstQuartile() {
    this.tech().trigger('adfirstquartile');
  }

  setupImaMidpointEvent() {
    this.addEventListener(google.ima.AdEvent.Type.MIDPOINT, this.onImaMidpoint.bind(this));
  }

  onImaMidpoint() {
    this.tech().trigger('admidpoint');
  }

  setupImaThirdQuartileEvent() {
    this.addEventListener(google.ima.AdEvent.Type.THIRD_QUARTILE, this.onImaThirdQuartile.bind(this));
  }

  onImaThirdQuartile() {
    this.tech().trigger('adthirdquartile');
  }

  replaceAdTag(tag){
    if (typeof this.ima().setContentWithAdTag === 'function') {
      this.ima().setContentWithAdTag(null, tag, true);
    }
  }
}
